/**
 * Collection Queries
 *
 * Generic ID-based fetch queries for CMS collections.
 * Used by blocks to fetch their own nested data instead of relying on deep page queries.
 *
 * Each query:
 * - Uses SolidStart's `query()` for automatic caching and deduplication
 * - Runs server-side with "use server" directive
 * - Returns fully populated data at the appropriate depth
 *
 * Block Implementation Pattern:
 * ```ts
 * import { isPopulated, extractIds } from "~/api/cms/blockDataUtils";
 * import { getChannelsByIdsQuery } from "~/api/cms/queries/collectionQueries";
 *
 * const MyBlock: Component<Props> = (props) => {
 *   const needsFetch = createMemo(() => !areChannelsPopulated(props.data.channels));
 *   const idsToFetch = createMemo(() => needsFetch() ? extractIds(props.data.channels) : []);
 *
 *   const fetched = createAsync(
 *     () => idsToFetch().length > 0 ? getChannelsByIdsQuery(idsToFetch()) : null,
 *     { deferStream: true }
 *   );
 *
 *   const resolved = createMemo(() => needsFetch() ? fetched() || [] : props.data.channels);
 *   return <Show when={resolved().length > 0}>...</Show>;
 * };
 * ```
 */

import { query } from "@solidjs/router";
import type {
  CMSChannelList,
  CMSSliderCollection,
  CMSChannel,
  CMSHost,
  CMSShow,
  CMSForm,
} from "~/types/cms";
import { logger } from "~/lib/logger/logger";
import { env } from "~/config/env";
import {
  buildCollectionByIdsUrl,
  buildSingleItemByIdUrl,
  buildShowsByChannelIdUrl,
  buildChannelShowsUrl,
} from "../queryUrls";
import { fetchFromCMS, reorderByIds } from "../queryUtils";

// ============================================================================
// Response Types
// ============================================================================

interface ChannelListsResponse {
  docs: CMSChannelList[];
  totalDocs: number;
}

interface ChannelsResponse {
  docs: CMSChannel[];
  totalDocs: number;
}

interface HostsResponse {
  docs: CMSHost[];
  totalDocs: number;
}

interface ShowsResponse {
  docs: CMSShow[];
  totalDocs: number;
}

// ============================================================================
// Channel Lists Queries
// ============================================================================

/**
 * Fetch channel lists by their IDs with full channel data populated.
 * depth=3: channels → branding → logo/images (fully populated media)
 *
 * Used by: HeroHomeBlock, ChannelBlock
 *
 * @param ids - Array of channel list IDs to fetch
 * @returns Array of fully populated channel lists
 */
export const getChannelListsByIdsQuery = query(async (ids: string[]) => {
  "use server";

  const LOG_PREFIX = "[channelListsByIds]";

  if (!ids || ids.length === 0) {
    logger.warn(`${LOG_PREFIX} No channel list IDs provided`);
    return [];
  }

  const url = `${env.cmsApiUrl}${buildCollectionByIdsUrl({ collection: "channel-lists", ids, depth: 3, tenantSlug: env.tenantSlug })}`;

  const data = await fetchFromCMS<ChannelListsResponse>(url, { logPrefix: LOG_PREFIX });

  if (!data?.docs || !Array.isArray(data.docs)) {
    logger.warn(`${LOG_PREFIX} Invalid response structure`);
    return [];
  }

  const orderedDocs = reorderByIds(data.docs, ids);
  logger.info(`${LOG_PREFIX} Fetched ${orderedDocs.length} channel lists`);
  return orderedDocs;
}, "channelListsByIds");

// ============================================================================
// Slider Queries
// ============================================================================

/**
 * Fetch a slider by its ID with full slide data populated.
 * depth=1: slides → media
 *
 * Used by: HeroHomeBlock, SliderBlock
 *
 * @param id - The slider ID to fetch
 * @returns The fully populated slider or null if not found
 */
export const getSliderByIdQuery = query(async (id: string | null) => {
  "use server";

  const LOG_PREFIX = "[sliderById]";

  if (!id) {
    return null;
  }

  const url = `${env.cmsApiUrl}${buildCollectionByIdsUrl({ collection: "sliders", ids: [id], depth: 1, tenantSlug: env.tenantSlug })}`;

  const data = await fetchFromCMS<{ docs: CMSSliderCollection[] }>(url, { logPrefix: LOG_PREFIX });

  if (!data?.docs || data.docs.length === 0) {
    logger.warn(`${LOG_PREFIX} Slider not found: ${id}`);
    return null;
  }

  logger.info(`${LOG_PREFIX} Fetched slider: ${data.docs[0].name || id}`);
  return data.docs[0];
}, "sliderById");

// ============================================================================
// Channel Queries
// ============================================================================

/**
 * Fetch channels by their IDs with branding data populated.
 * depth=2: branding → media (logos, images fully populated)
 *
 * Used by: ChannelBlock, CardGridBlock
 *
 * @param ids - Array of channel IDs to fetch
 * @returns Array of fully populated channels
 */
export const getChannelsByIdsQuery = query(async (ids: string[]) => {
  "use server";

  const LOG_PREFIX = "[channelsByIds]";

  if (!ids || ids.length === 0) {
    return [];
  }

  const url = `${env.cmsApiUrl}${buildCollectionByIdsUrl({ collection: "channels", ids, depth: 2, filterPublished: true, tenantSlug: env.tenantSlug })}`;

  const data = await fetchFromCMS<ChannelsResponse>(url, { logPrefix: LOG_PREFIX });

  if (!data?.docs) {
    logger.warn(`${LOG_PREFIX} No channels found for IDs: ${ids.join(", ")}`);
    return [];
  }

  const orderedDocs = reorderByIds(data.docs, ids);
  logger.info(`${LOG_PREFIX} Fetched ${orderedDocs.length} channels`);
  return orderedDocs;
}, "channelsByIds");

// ============================================================================
// Host Queries
// ============================================================================

/**
 * Fetch hosts by their IDs with shows data populated.
 * depth=2: shows → channel → branding
 *
 * Used by: CardGridBlock, HeroPodcastBlock
 *
 * @param ids - Array of host IDs to fetch
 * @returns Array of fully populated hosts
 */
export const getHostsByIdsQuery = query(async (ids: string[]) => {
  "use server";

  const LOG_PREFIX = "[hostsByIds]";

  if (!ids || ids.length === 0) {
    return [];
  }

  const url = `${env.cmsApiUrl}${buildCollectionByIdsUrl({ collection: "hosts", ids, depth: 2, tenantSlug: env.tenantSlug })}`;

  const data = await fetchFromCMS<HostsResponse>(url, { logPrefix: LOG_PREFIX });

  if (!data?.docs) {
    logger.warn(`${LOG_PREFIX} No hosts found for IDs: ${ids.join(", ")}`);
    return [];
  }

  const orderedDocs = reorderByIds(data.docs, ids);
  logger.info(`${LOG_PREFIX} Fetched ${orderedDocs.length} hosts`);
  return orderedDocs;
}, "hostsByIds");

// ============================================================================
// Show Queries
// ============================================================================

/**
 * Fetch shows by their IDs with channel and hosts data populated.
 * depth=2: channel → branding, hosts → image
 *
 * Used by: CardGridBlock, StationShowsBlock
 *
 * @param ids - Array of show IDs to fetch
 * @returns Array of fully populated shows
 */
export const getShowsByIdsQuery = query(async (ids: string[]) => {
  "use server";

  const LOG_PREFIX = "[showsByIds]";

  if (!ids || ids.length === 0) {
    return [];
  }

  const url = `${env.cmsApiUrl}${buildCollectionByIdsUrl({ collection: "shows", ids, depth: 2, filterPublished: true, tenantSlug: env.tenantSlug })}`;

  const data = await fetchFromCMS<ShowsResponse>(url, { logPrefix: LOG_PREFIX });

  if (!data?.docs) {
    logger.warn(`${LOG_PREFIX} No shows found for IDs: ${ids.join(", ")}`);
    return [];
  }

  const orderedDocs = reorderByIds(data.docs, ids);
  logger.info(`${LOG_PREFIX} Fetched ${orderedDocs.length} shows`);
  return orderedDocs;
}, "showsByIds");

// ============================================================================
// Show Queries by Channel
// ============================================================================

/**
 * Fetch shows for a specific channel with calendar events and images.
 * depth=2: calendarEvents → eventImage
 *
 * Used by: StationShowsBlockRenderer (self-fetching pattern)
 *
 * @param channelId - The channel ID to fetch shows for
 * @returns Array of fully populated shows
 */
export const getShowsByChannelIdQuery = query(async (channelId: string) => {
  "use server";

  const LOG_PREFIX = "[showsByChannelId]";

  if (!channelId) {
    logger.warn(`${LOG_PREFIX} No channel ID provided`);
    return [];
  }

  const url = `${env.cmsApiUrl}${buildShowsByChannelIdUrl({ channelId, tenantSlug: env.tenantSlug })}`;

  const data = await fetchFromCMS<ShowsResponse>(url, { logPrefix: LOG_PREFIX });

  if (!data?.docs) {
    logger.warn(`${LOG_PREFIX} No shows found for channel: ${channelId}`);
    return [];
  }

  logger.info(`${LOG_PREFIX} Fetched ${data.docs.length} shows for channel ${channelId}`);
  return data.docs;
}, "showsByChannelId");

/**
 * Fetch a channel's shows in the editor-curated order (ADE-859).
 *
 * Reads `channel.shows` from the channel side, which Payload returns in the relationship's
 * stored (drag) order — so both membership and order come straight from the query, with no
 * client-side rerank. Only populated, published shows are kept; array order is preserved.
 *
 * Used by: StationShowsBlockRenderer (self-fetching pattern)
 *
 * @param channelId - The channel whose ordered shows to fetch
 * @returns Array of populated, published shows in channel (drag) order
 */
export const getChannelShowsQuery = query(async (channelId: string) => {
  "use server";

  const LOG_PREFIX = "[channelShows]";

  if (!channelId) {
    logger.warn(`${LOG_PREFIX} No channel ID provided`);
    return [] as CMSShow[];
  }

  const url = `${env.cmsApiUrl}${buildChannelShowsUrl({ channelId })}`;

  const data = await fetchFromCMS<CMSChannel>(url, { logPrefix: LOG_PREFIX });

  const shows = Array.isArray(data?.shows) ? data.shows : [];
  // Keep only populated, published shows; preserve the channel's array order.
  const published = shows.filter(
    (s): s is CMSShow => typeof s === "object" && s !== null && s._status === "published"
  );

  logger.info(`${LOG_PREFIX} Fetched ${published.length} shows for channel ${channelId}`);
  return published;
}, "channelShows");

// ============================================================================
// Host Queries by Channel
// ============================================================================

/**
 * Fetch hosts for a specific channel (via shows relationship).
 * depth=2: shows → channel for relationship traversal
 *
 * Used by: StationHostsBlockRenderer (self-fetching pattern)
 *
 * NOTE: We can't directly query hosts by channel since hosts relate to shows,
 * not channels. So we fetch shows first, then collect unique hosts.
 *
 * @param channelId - The channel ID to fetch hosts for
 * @returns Array of fully populated hosts
 */
export const getHostsByChannelIdQuery = query(async (channelId: string) => {
  "use server";

  const LOG_PREFIX = "[hostsByChannelId]";

  if (!channelId) {
    logger.warn(`${LOG_PREFIX} No channel ID provided`);
    return [];
  }

  const showsUrl = `${env.cmsApiUrl}${buildShowsByChannelIdUrl({ channelId, tenantSlug: env.tenantSlug })}`;
  const showsData = await fetchFromCMS<ShowsResponse>(showsUrl, { logPrefix: LOG_PREFIX });

  if (!showsData?.docs || showsData.docs.length === 0) {
    logger.warn(`${LOG_PREFIX} No shows found for channel: ${channelId}`);
    return [];
  }

  // Collect unique hosts from all shows
  const hostMap = new Map<string, CMSHost>();
  for (const show of showsData.docs) {
    if (show.hosts && Array.isArray(show.hosts)) {
      for (const host of show.hosts) {
        if (typeof host === "object" && host !== null && host.id) {
          hostMap.set(String(host.id), host);
        }
      }
    }
  }

  const hosts = Array.from(hostMap.values());
  logger.info(
    `${LOG_PREFIX} Collected ${hosts.length} unique hosts from ${showsData.docs.length} shows`
  );
  return hosts;
}, "hostsByChannelId");

// ============================================================================
// Form Queries
// ============================================================================

/**
 * Fetch a form by its ID with full field configuration.
 * depth=1: sufficient for form fields configuration
 *
 * Used by: FormBlock
 *
 * @param id - The form ID to fetch
 * @returns The fully populated form or null if not found
 */
export const getFormByIdQuery = query(async (id: string | null) => {
  "use server";

  const LOG_PREFIX = "[formById]";

  if (!id) {
    return null;
  }

  const url = `${env.cmsApiUrl}${buildSingleItemByIdUrl({ collection: "forms", id, depth: 1 })}`;

  const data = await fetchFromCMS<CMSForm>(url, { logPrefix: LOG_PREFIX });

  if (!data) {
    logger.warn(`${LOG_PREFIX} Form not found: ${id}`);
    return null;
  }

  logger.info(`${LOG_PREFIX} Fetched form: ${data.title || id}`);
  return data;
}, "formById");
